import React from "react";
import { Card, Alert, Badge } from "react-bootstrap";

function AuditLog() {
  return (
    <div>
      <h2 className="mb-4">
        Audit Log
      </h2>

      <Alert variant="info">
        Track and review all system activities, user actions, and security events for compliance and monitoring.
      </Alert>

      <Card className="mb-4">
        <Card.Body>
          <h4>Overview</h4>
          <p>
            The Audit Log provides a comprehensive record of all activities in the system. Every significant
            action is logged with details about who performed it, when, and what changed.
          </p>
          <p className="mt-3">
            <strong>Key Features:</strong>
          </p>
          <ul>
            <li>Track user authentication and access events</li>
            <li>Monitor runbook executions and modifications</li>
            <li>Review permission and group changes</li>
            <li>See variable and connector updates</li>
            <li>Filter and search by multiple criteria</li>
            <li>View before/after values for changes</li>
            <li>Export logs to Excel for reporting</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Accessing the Audit Log</h4>
          <p>
            The Audit Log is available to administrators with the appropriate permissions:
          </p>
          <ol>
            <li>Navigate to <strong>Settings</strong> in the main menu</li>
            <li>Select <strong>Audit Log</strong></li>
            <li>The most recent entries will be displayed first</li>
          </ol>

          <Alert variant="warning" className="mt-3">
            <strong>Permission Required:</strong> You must be an Administrator to view audit logs.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Log Entries</h4>
          <p>Each audit log entry contains the following information:</p>

          <h6 className="mt-3">Basic Information</h6>
          <ul>
            <li><strong>Created On</strong> - Date and time the action occurred</li>
            <li><strong>Email</strong> - User who performed the action</li>
            <li><strong>Action Type</strong> - Type of action (e.g., "Runbook Executed", "User Created")</li>
            <li><strong>Resource</strong> - What was affected (type and name/ID)</li>
            <li><strong>Status</strong> - <Badge bg="success">Success</Badge> or <Badge bg="danger">Failed</Badge></li>
            <li><strong>IP Address</strong> - Network address of the user</li>
          </ul>

          <h6 className="mt-3">Detailed Information (Click on row)</h6>
          <p>When you click on an audit log entry, you'll see additional details:</p>
          <ul>
            <li><strong>Audit Log ID</strong> - Unique identifier for this log entry</li>
            <li><strong>Description</strong> - Detailed description of what happened</li>
            <li><strong>Resource Type & ID</strong> - Specific resource that was affected</li>
            <li><strong>Authentication Method</strong> - How the user authenticated (CloudSync, Okta, Azure AD)</li>
            <li><strong>Old Value</strong> - Previous value before the change (if applicable)</li>
            <li><strong>New Value</strong> - Updated value after the change (if applicable)</li>
            <li><strong>Metadata</strong> - Additional context about the action</li>
            <li><strong>Error Details</strong> - Error message and code (if action failed)</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Action Types</h4>
          <p>The audit log tracks many types of actions, organized by category:</p>

          <h6 className="mt-3">Authentication & Access</h6>
          <ul>
            <li>Login Success / Failed</li>
            <li>MFA Success / Failed</li>
            <li>Logout</li>
            <li>Token Refresh</li>
            <li>Impersonation Started / Ended</li>
          </ul>

          <h6 className="mt-3">User Management</h6>
          <ul>
            <li>User Created / Updated / Deleted</li>
            <li>User Password Changed</li>
            <li>User Status Changed</li>
          </ul>

          <h6 className="mt-3">Runbook Operations</h6>
          <ul>
            <li>Runbook Created / Updated / Deleted</li>
            <li>Runbook Executed</li>
            <li>Runbook Published / Archived</li>
            <li>Runbook Version Created / Updated / Deleted</li>
            <li>Runbook Version Activated / Deactivated</li>
          </ul>

          <h6 className="mt-3">Variable Operations</h6>
          <ul>
            <li>Variable Created / Updated / Deleted</li>
            <li>Variable Line Created / Updated / Deleted</li>
          </ul>

          <h6 className="mt-3">Permission & Access Control</h6>
          <ul>
            <li>Permission Group Created / Updated / Deleted</li>
            <li>User Added to Group / Removed from Group</li>
            <li>Permission Assigned / Revoked</li>
          </ul>

          <h6 className="mt-3">Connector Operations</h6>
          <ul>
            <li>Connector Created / Updated / Deleted</li>
            <li>Connector Secret Created / Updated</li>
            <li>Connector Secret Retrieved</li>
            <li>Connector Auth Tested</li>
          </ul>

          <h6 className="mt-3">System & Security</h6>
          <ul>
            <li>System Setting Changed</li>
            <li>Data Exported / Imported</li>
            <li>Unauthorized Access Attempt</li>
            <li>Suspicious Activity</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Filtering Audit Logs</h4>
          <p>
            Use the filter panel at the top to narrow down audit logs. Click "Click here to expand filters" if collapsed.
          </p>

          <h6 className="mt-3">Available Filters</h6>
          <ul>
            <li><strong>User Email</strong> - Filter by specific user</li>
            <li><strong>IP Address</strong> - Filter by network address</li>
            <li><strong>Status</strong> - Show only Success or Failed actions</li>
            <li><strong>Action Type</strong> - Filter by specific type (organized by category)</li>
            <li><strong>Resource Type</strong> - Filter by affected resource (Runbook, User, Variable, etc.)</li>
            <li><strong>Resource ID</strong> - Filter by specific resource identifier</li>
            <li><strong>Authentication Method</strong> - Filter by how users logged in</li>
            <li><strong>Date Range</strong> - Start Date and End Date for time-based filtering</li>
          </ul>

          <h6 className="mt-3">Using Filters</h6>
          <ol>
            <li>Click "Click here to expand filters" to show the filter panel</li>
            <li>Enter or select your filter criteria</li>
            <li>Click the <strong>Search</strong> button to apply filters</li>
            <li>Click <strong>Clear filters</strong> to reset and show all logs</li>
            <li>Click "Click here to collapse filters" to hide the panel</li>
          </ol>

          <p className="mt-3">
            <strong>Tip:</strong> Combine multiple filters for precise results. For example, filter by User Email + Action Type + Date Range
            to see all runbook executions by a specific user in the last month.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Common Use Cases</h4>

          <h6 className="mt-3">Security Monitoring</h6>
          <ul>
            <li><strong>Failed Login Attempts</strong> - Filter by "Login Failed" to identify potential security threats</li>
            <li><strong>Unauthorized Access</strong> - Search for "Unauthorized Access Attempt" actions</li>
            <li><strong>User Activity</strong> - Review all actions by a specific user's email</li>
            <li><strong>After-Hours Activity</strong> - Use date/time filters to check activity outside business hours</li>
            <li><strong>IP Address Tracking</strong> - Monitor access from specific networks or unexpected locations</li>
          </ul>

          <h6 className="mt-3">Compliance & Auditing</h6>
          <ul>
            <li><strong>Permission Changes</strong> - Track when permissions or groups were modified</li>
            <li><strong>Data Access</strong> - Review who accessed sensitive variables or connectors</li>
            <li><strong>User Lifecycle</strong> - See when users were created, modified, or deleted</li>
            <li><strong>Configuration Changes</strong> - Monitor system settings and integration updates</li>
            <li><strong>Export Reports</strong> - Export filtered logs to Excel for compliance documentation</li>
          </ul>

          <h6 className="mt-3">Operational Insights</h6>
          <ul>
            <li><strong>Runbook Usage</strong> - Filter by "Runbook Executed" to see which workflows are most used</li>
            <li><strong>Error Patterns</strong> - Filter by Status: Failed to identify recurring issues</li>
            <li><strong>Change History</strong> - See who modified runbooks, variables, or connectors and when</li>
            <li><strong>User Adoption</strong> - Track login patterns and feature usage</li>
          </ul>

          <h6 className="mt-3">Troubleshooting</h6>
          <ul>
            <li><strong>Failed Actions</strong> - Review error messages to diagnose problems</li>
            <li><strong>Timeline Reconstruction</strong> - See sequence of events leading to an issue</li>
            <li><strong>Change Tracking</strong> - Use Old Value/New Value to see exactly what changed</li>
            <li><strong>User Reports</strong> - Verify user claims about access or actions</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Example Scenarios</h4>

          <h6 className="mt-3">Scenario 1: Investigating Failed Login</h6>
          <ol>
            <li>Expand the filter panel</li>
            <li>Select Action Type: <strong>Login Failed</strong></li>
            <li>Optionally enter the user's email if known</li>
            <li>Set a date range if needed</li>
            <li>Click <strong>Search</strong></li>
            <li>Review the log entries and IP addresses</li>
            <li>Look for patterns (same IP, multiple attempts, etc.)</li>
          </ol>

          <h6 className="mt-3">Scenario 2: Auditing Runbook Changes</h6>
          <ol>
            <li>Expand the filter panel</li>
            <li>Select Resource Type: <strong>Runbook</strong> or <strong>Runbook Version</strong></li>
            <li>Optionally enter the specific Runbook ID</li>
            <li>Select Action Type: <strong>Runbook Updated</strong></li>
            <li>Click <strong>Search</strong></li>
            <li>Click on entries to see Old Value and New Value</li>
            <li>Review who made changes and when</li>
          </ol>

          <h6 className="mt-3">Scenario 3: User Activity Review</h6>
          <ol>
            <li>Expand the filter panel</li>
            <li>Enter the user's email address</li>
            <li>Set a date range (e.g., last 30 days)</li>
            <li>Click <strong>Search</strong></li>
            <li>Review all actions performed by this user</li>
            <li>Export to Excel if needed for documentation</li>
          </ol>

          <h6 className="mt-3">Scenario 4: Tracking Variable Changes</h6>
          <ol>
            <li>Expand the filter panel</li>
            <li>Select Resource Type: <strong>Variable Line</strong></li>
            <li>Select Action Type: <strong>Variable Line Updated</strong></li>
            <li>Click <strong>Search</strong></li>
            <li>Click on entries to see Old Value and New Value comparison</li>
            <li>Identify who changed sensitive configuration data</li>
          </ol>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Exporting Audit Logs</h4>
          <p>
            You can export filtered audit logs to Excel for reporting or archiving:
          </p>
          <ol>
            <li>Apply your desired filters to narrow down the logs</li>
            <li>Click the <strong>Export to Excel</strong> button (if available)</li>
            <li>The export will include all visible columns from the current filter</li>
            <li>Use the Excel file for compliance reports, offline analysis, or long-term archiving</li>
          </ol>

          <p className="mt-3">
            <strong>Note:</strong> The export includes only the data currently visible in the grid based on your filters.
            To export all logs, clear all filters before exporting.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Understanding Change Tracking</h4>
          <p>
            For actions that modify data, the audit log captures both old and new values:
          </p>

          <h6 className="mt-3">When Available</h6>
          <p>Old Value and New Value fields appear for:</p>
          <ul>
            <li>Variable Line updates (key/value changes)</li>
            <li>Runbook Version changes (locked status, activation, etc.)</li>
            <li>User profile updates</li>
            <li>Permission group modifications</li>
            <li>System setting changes</li>
          </ul>

          <h6 className="mt-3">How to View</h6>
          <ol>
            <li>Click on any audit log entry in the grid</li>
            <li>Look for the <strong>Change Details</strong> section</li>
            <li>See Old Value on the left, New Value on the right</li>
            <li>Both values are preserved for audit purposes</li>
          </ol>

          <Alert variant="info" className="mt-3">
            <strong>Privacy Note:</strong> Sensitive data like passwords and connector secrets are never logged in plain text.
            You'll see that the action occurred, but not the actual secret values.
          </Alert>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Best Practices</h4>
          <ul>
            <li><strong>Regular Reviews</strong> - Check audit logs weekly for unusual activity</li>
            <li><strong>Failed Actions</strong> - Investigate all failed authentication attempts</li>
            <li><strong>Date Filters</strong> - Use date ranges to focus on specific time periods</li>
            <li><strong>Export Important Events</strong> - Save exports of significant changes or incidents</li>
            <li><strong>Security Patterns</strong> - Watch for repeated failures or suspicious IP addresses</li>
            <li><strong>Compliance Documentation</strong> - Use audit logs as evidence for security audits</li>
            <li><strong>Change Management</strong> - Review logs before and after major system changes</li>
            <li><strong>User Training</strong> - Use logs to identify areas where users need more guidance</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Data Retention</h4>
          <p>
            Audit log retention policies may vary by installation:
          </p>
          <ul>
            <li>Logs are typically retained for compliance requirements (90 days, 1 year, etc.)</li>
            <li>Contact your system administrator for specific retention policies</li>
            <li>Export important logs to Excel for longer-term archiving if needed</li>
            <li>Older logs may be archived or purged based on your organization's policies</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Limitations</h4>
          <ul>
            <li><strong>Client Scope</strong> - You can only see logs for your client organization</li>
            <li><strong>Admin Access</strong> - Admins can see logs across all clients if "All Clients" is selected</li>
            <li><strong>Real-Time Only</strong> - No historical archival beyond retention period</li>
            <li><strong>No Log Modification</strong> - Audit logs cannot be edited or deleted (by design)</li>
            <li><strong>Performance</strong> - Very large date ranges may take longer to load</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Related Topics</h4>
          <p>For more information on related features, see:</p>
          <ul>
            <li><strong>User Management</strong> - Managing user accounts and access</li>
            <li><strong>Permission Groups Overview</strong> - Understanding the permission system</li>
            <li><strong>Permission Lookups</strong> - Finding user permissions and runbook access</li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Body>
          <h4>Troubleshooting</h4>

          <h6 className="mt-3">I don't see the Audit Log menu item</h6>
          <p>
            You need to be an Administrator. Contact your system administrator
            if you need access to audit logs.
          </p>

          <h6 className="mt-3">Filters aren't working</h6>
          <p>
            Make sure you click the <strong>Search</strong> button after entering filter criteria. The filters
            don't apply automatically.
          </p>

          <h6 className="mt-3">Action Type shows "Unknown Action Type"</h6>
          <p>
            This may indicate a new action type that hasn't been mapped yet. Note the numeric ID and contact support
            if you see many unknown types.
          </p>

          <h6 className="mt-3">Can't see old audit logs</h6>
          <p>
            Check your date range filters. Also verify the data retention policy - older logs may have been
            archived or purged based on compliance requirements.
          </p>
        </Card.Body>
      </Card>
    </div>
  );
}

export default AuditLog;
